import React, { useCallback } from 'react';
import {
  IExpressionCohortFieldsFragment,
  TimeWindowKey,
} from '../../../../utils/apollo/graphqlSchema';
import * as BuilderInfoUI from '../../BuilderInfoUI';
import InfoPopupHeaderSetting from '../InfoPopupHeaderSetting';
import { CohortConditionIcon } from '../../../Icon';
import { getCohortPercentileFilterLabel } from '../../../../utils/builder/cohorts';
import CohortDetailsDateRange from './CohortDetailsDateRange';
import { assertCondition, assertNotNil } from '../../../../utils/types';
import { generateExpressionCohortMeasureOptions } from '../../../Builder/Cohorts/CohortRow/utils';
import { parseAliasReplacements } from '../../../../utils/redux/util';
import {
  CohortDetailsBorderedInfo,
  CohortDetailsContent,
} from './CohortDetailsUI';

interface IProps {
  cohort: IExpressionCohortFieldsFragment;
  cohortIndex: number;
}

export default ({ cohort, cohortIndex }: IProps) => {
  const {
    type,
    operator,
    timeWindow,
    timeWindowValues,
    operatorValues,
    analysis,
    percentileFilter,
    datePicker,
    subject,
  } = cohort;
  const isCompoundMeasure =
    analysis.query.measure.__typename === 'CompoundMeasure';
  const getSelectedMeasureName = useCallback(
    (measureId: string) => {
      if (!isCompoundMeasure) {
        return;
      }

      assertCondition(analysis.query.measure.__typename === 'CompoundMeasure');
      const parsedAliasReplacements = parseAliasReplacements(
        analysis.pref.aliasReplacements,
      );
      const measureOptions = generateExpressionCohortMeasureOptions(
        analysis.query.measure,
        parsedAliasReplacements,
      );

      const measureResult = measureOptions.find(
        (option) => option.value === measureId,
      );
      // selected measure id should always be in query.measure
      assertNotNil(measureResult);
      return measureResult.text;
    },
    [isCompoundMeasure, analysis],
  );

  return (
    <>
      <BuilderInfoUI.HeaderContainer>
        <CohortConditionIcon conditionIndex={cohortIndex} />
        <InfoPopupHeaderSetting label={'Subject'}>
          {subject.name}
        </InfoPopupHeaderSetting>
        <InfoPopupHeaderSetting label={'Type'}>
          {type.name}
        </InfoPopupHeaderSetting>
        <CohortDetailsDateRange datePicker={datePicker} />
      </BuilderInfoUI.HeaderContainer>
      <CohortDetailsContent>
        <BuilderInfoUI.HighlightedLabel>Query</BuilderInfoUI.HighlightedLabel>
        <CohortDetailsBorderedInfo>
          <BuilderInfoUI.StyledPopupLabel>
            {analysis.display}
          </BuilderInfoUI.StyledPopupLabel>
        </CohortDetailsBorderedInfo>
        {isCompoundMeasure && (
          <CohortDetailsBorderedInfo>
            {getSelectedMeasureName(cohort.measureId)}
          </CohortDetailsBorderedInfo>
        )}
        {operatorValues?.length > 0 && (
          <>
            <CohortDetailsBorderedInfo>
              {operator.name}
            </CohortDetailsBorderedInfo>
            <CohortDetailsBorderedInfo>
              {operatorValues[0]}
            </CohortDetailsBorderedInfo>
            {operatorValues.length === 2 && (
              <>
                <div>~</div>
                <CohortDetailsBorderedInfo>
                  {operatorValues[1]}
                </CohortDetailsBorderedInfo>
              </>
            )}
          </>
        )}
        {percentileFilter && (
          <InfoPopupHeaderSetting>
            {getCohortPercentileFilterLabel({
              operatorKey: percentileFilter.operator.key,
              operatorName: percentileFilter.operator.name,
              values: percentileFilter.values,
            })}
          </InfoPopupHeaderSetting>
        )}
        <CohortDetailsBorderedInfo>{timeWindow.name}</CohortDetailsBorderedInfo>
        {timeWindowValues?.length > 0 && (
          <>
            <CohortDetailsBorderedInfo>
              {timeWindowValues[0]}
            </CohortDetailsBorderedInfo>
            {timeWindowValues.length === 2 && (
              <>
                <div>and</div>
                <CohortDetailsBorderedInfo>
                  {timeWindowValues[1]}
                </CohortDetailsBorderedInfo>
              </>
            )}
          </>
        )}
        {timeWindow.key !== TimeWindowKey.Overall && <div>day(s)</div>}
      </CohortDetailsContent>
    </>
  );
};
